import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import axios from 'axios';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TrackingService {

  private url = environment.server;
  private skipRoutes = ['/login', '/reset-password'];
  private currentPath = '/';

  // Separates "which page" from "what was clicked" inside a single event_detail
  // string, so we don't need a DB schema change to know the page context of a
  // button click. Kept short/unique so it never collides with real click text.
  static readonly DETAIL_DELIM = ' ::: ';

  constructor(private router: Router, private authService: AuthService) {
    this.currentPath = this.router.url || '/';

    // Auto-track page views on navigation
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      const path = event.urlAfterRedirects || event.url;
      this.currentPath = path;
      if (!this.skipRoutes.some(r => path.startsWith(r))) {
        this.sendEvent('page_view', path);
      }
    });

    // Auto-track clicks on interactive elements (buttons, links, menu items)
    document.addEventListener('click', (e: MouseEvent) => {
      try {
        const target = e.target as HTMLElement;
        if (!target) return;

        // Walk up to find the closest interactive element
        const interactive = target.closest('a, button, [role="menuitem"], [routerLink], [click]');
        if (!interactive) return;

        const detail = this.getClickDetail(interactive as HTMLElement);
        if (detail) {
          // Prefix with the current page so the activity log can show
          // "where" a click happened, not just "what" was clicked.
          this.sendEvent('button_click', `${this.currentPath}${TrackingService.DETAIL_DELIM}${detail}`);
        }
      } catch {
        // silently ignore
      }
    }, true); // capture phase to catch everything
  }

  trackClick(buttonId: string): void {
    this.sendEvent('button_click', `${this.currentPath}${TrackingService.DETAIL_DELIM}${buttonId}`);
  }

  private getClickDetail(el: HTMLElement): string {
    // Priority 0: explicit developer-provided label/zone (most reliable & descriptive).
    // Add data-track-label="..." to a button and/or data-track-zone="..." to a
    // wrapping section to make ambiguous UI (icon-only buttons, repeated
    // "Save"/"Delete" buttons, etc.) show up clearly in the activity log.
    const labelEl = el.closest('[data-track-label]');
    const zoneEl = el.closest('[data-track-zone]');
    const zone = zoneEl ? zoneEl.getAttribute('data-track-zone') : null;

    const base = labelEl ? (labelEl.getAttribute('data-track-label') || '') : this.inferClickLabel(el);
    if (!base) return '';

    return zone ? `${zone} › ${base}` : base;
  }

  private inferClickLabel(el: HTMLElement): string {
    // Priority 1: routerLink attribute
    const routerLink = el.getAttribute('routerLink') || el.getAttribute('ng-reflect-router-link');
    if (routerLink) {
      const text = this.getCleanText(el);
      return text ? `[${routerLink}] ${text}` : routerLink;
    }

    // Priority 2: element id
    const id = el.getAttribute('id');

    // Priority 3: clean text content
    const text = this.getCleanText(el);

    // Priority 4: aria-label
    const ariaLabel = el.getAttribute('aria-label');

    // Build the detail string
    const parts: string[] = [];
    if (id) parts.push(`#${id}`);
    if (text) parts.push(text);
    else if (ariaLabel) parts.push(ariaLabel);

    if (parts.length === 0) {
      // Fallback: tag + class hint
      const tag = el.tagName.toLowerCase();
      const cls = el.className ? String(el.className).split(' ').slice(0, 2).join('.') : '';
      return cls ? `${tag}.${cls}` : tag;
    }

    return parts.join(' ');
  }

  private getCleanText(el: HTMLElement): string {
    // Get text content but skip SVG innards and excessive whitespace
    const clone = el.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('svg, img').forEach(n => n.remove());
    const text = (clone.textContent || '').replace(/\s+/g, ' ').trim();
    return text.substring(0, 100); // cap at 100 chars
  }

  private async sendEvent(eventType: string, eventDetail: string): Promise<void> {
    try {
      const user = this.authService.getUserData();
      if (!user) return;
      const token = await user.getIdToken();
      axios.post(`${this.url}/dashboard/user-events`, {
        event_type: eventType,
        event_detail: eventDetail
      }, {
        headers: { Authorization: 'Bearer ' + token }
      }).catch(() => {}); // fire-and-forget
    } catch {
      // silently ignore all errors
    }
  }
}
